'use client';

import { getConfig } from '@/config';
import type { locales } from '@/locales';
import { localesToDirectusMap } from '@/locales';
import { useRouter } from '@/navigation';
import UserActions from '@/server/actions/user';
import useNotifications from '@/stores/notifications';
import type { ServerActionFailedResponse, UserParams } from '@/types';
import type { Me } from '@/types/me';
import type { API10, Directus } from '@gourmetta-bestellsystem/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export const QUERY_KEY = ['user'] as const;

export const useMe = () => {
  const { addNotification } = useNotifications();
  return useQuery<Me, ServerActionFailedResponse['error']>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await UserActions.getMe();
      if (!response.success) {
        addNotification(response.error.message, 'error');
        throw response.error;
      }
      return response.data;
    },
  });
};

export const useUserSignUp = () => {
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false);
  // Initialize timestamp when hook first loads (form mount time)
  const [formLoadedAt] = useState(() => Date.now());
  const config = getConfig();
  const { addNotification } = useNotifications();
  const router = useRouter();
  const locale = useLocale();
  const tForms = useTranslations('forms');

  const schema = z
    .object({
      firstName: z
        .string()
        .min(1, { message: tForms('error-firstname') })
        .max(255),
      lastName: z
        .string()
        .min(1, { message: tForms('error-lastname') })
        .max(255),
      email: z
        .string()
        .email({ message: tForms('error-email') })
        .min(1, { message: tForms('error-email') })
        .max(255),
      password: z
        .string()
        .max(255, {
          message: tForms('error-password-max-length'),
        })
        .refine(
          (value) =>
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/.test(
              value,
            ),
          tForms('error-password-strength'),
        ),
      passwordConfirmation: z.string(),
      kitchenName: z
        .string()
        .min(1, { message: tForms('error-kitchen') })
        .max(255),
      branchOfficeId: z
        .string()
        .min(1, {
          message: tForms('error-branch-office-id'),
        })
        .max(255),
      supervisorName: z
        .string()
        .min(1, { message: tForms('error-supervisor-name') })
        .max(255),
      termsAndConditions: z.boolean().refine((value) => value === true, {
        message: tForms('error-terms-and-conditions'),
      }),
      // Honeypot fields - should remain empty for legitimate users
      companyWebsite: z.string().optional(),
      emailConfirm: z.string().optional(),
    })
    .refine((data) => data.password === data.passwordConfirmation, {
      message: tForms('error-password-repeat-match'),
      path: ['passwordConfirmation'],
    });

  const {
    handleSubmit,
    register: registerFormModel,
    formState: { errors: formErrors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  const {
    mutate,
    isPending: isLoading,
    isError,
  } = useMutation<
    API10.UserSignup.Response,
    ServerActionFailedResponse['error'],
    z.infer<typeof schema>
  >({
    mutationFn: async (data) => {
      const hpWebsite = data.companyWebsite?.trim();
      const hpEmail = data.emailConfirm?.trim();
      const response = await UserActions.userSignUp({
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        password: data.password,
        language: locale === 'de' ? 'de-DE' : 'fr-FR',
        origin: config.registrationOrigin,
        // Honeypot fields - only sent if filled (by bots)
        ...(hpWebsite && { hp_website: hpWebsite }),
        ...(hpEmail && { hp_email: hpEmail }),
        // Timing check - always required
        form_loaded_at: formLoadedAt,
        registration_comment:
          'Name der Küche: ' +
          data.kitchenName +
          '\n' +
          'Kennziffer der Außenstelle: ' +
          data.branchOfficeId +
          '\n' +
          'Name des Supervisors: ' +
          data.supervisorName,
      });
      if (!response.success) {
        if (response.error.origin.code === 'USER_ALREADY_EXISTS') {
          setEmailAlreadyExists(true);
        } else {
          addNotification(response.error.message, 'error');
        }
        throw response.error;
      }
      router.push('/sign-up-success', { locale });
      return response.data;
    },
  });

  const onSubmit = handleSubmit(async (data) => mutate(data));

  return {
    isLoading,
    isError,
    emailAlreadyExists,
    onSubmit,
    registerFormModel,
    formErrors,
  };
};

export const useUpdateUser = () => {
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();
  return useMutation<
    Directus.DirectusUser,
    ServerActionFailedResponse['error'],
    UserParams.UpdateUser
  >({
    mutationFn: async (params) => {
      const response = await UserActions.updateUser(params);
      if (!response.success) {
        addNotification(response.error.message, 'error');
        throw response.error;
      }
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });

      return response.data;
    },
  });
};

export const useResetPasswordRequestEmail = () => {
  const { addNotification } = useNotifications();
  const router = useRouter();
  const locale = useLocale();
  return useMutation<
    void,
    ServerActionFailedResponse['error'],
    API10.ResetPassword.RequestEmail.Body
  >({
    mutationFn: async (params) => {
      const language =
        params.language ??
        localesToDirectusMap[locale as (typeof locales)[number]];
      const response = await UserActions.resetPasswordRequestEmail({
        ...params,
        language,
      });
      if (!response.success) {
        addNotification(response.error.message, 'error');
        throw response.error;
      }
      router.push(
        { pathname: '/reset-password', query: { action: 'emailSent' } },
        { locale },
      );
    },
  });
};

export const useResetPasswordResetPassword = () => {
  const { addNotification } = useNotifications();
  const locale = useLocale();
  const router = useRouter();
  return useMutation<
    void,
    ServerActionFailedResponse['error'],
    API10.ResetPassword.ResetPassword.Body
  >({
    mutationFn: async (params) => {
      const response = await UserActions.resetPasswordResetPassword(params);
      if (!response.success) {
        addNotification(response.error.message, 'error');
        throw response.error;
      }
      router.push(
        { pathname: '/reset-password', query: { action: 'passwordChanged' } },
        { locale },
      );
    },
  });
};
